import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { environment } from '@environments/environment';
import type { FilterResponse } from '../models';
import { Storage } from '../services/storage';
import { AccountStore } from '../stores/account-store';
import { NotificationService } from '../services/notification.service';
import { LoggerService } from '../services/logger.service';

/**
 * TransactionFilterApi - HTTP API calls for transaction filters
 *
 * Handles filter options retrieval:
 * - Get available filter values (accounts, statuses, types, categories)
 *
 * Security:
 * - Uses legacy API with static bearer token from environment
 * - Auto-injects merchant credentials from localStorage
 */
@Injectable({
  providedIn: 'root',
})
export class TransactionFilterApi {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(Storage);
  private readonly accountStore = inject(AccountStore);
  private readonly notification = inject(NotificationService);
  private readonly logger = inject(LoggerService);

  /**
   * Get available filter options from API
   * Automatically retrieves credentials from localStorage
   *
   * @returns Observable<FilterResponse>
   * @throws Error if user data not found in storage
   *
   * @example
   * ```typescript
   * this.filterApi.getFilters().subscribe(filters => {
   *   console.log('Available statuses:', filters.statuses);
   *   console.log('Available categories:', filters.categories);
   * });
   * ```
   */
  getFilters(): Observable<FilterResponse> {
    const userData = this.storage.getUserData();
    if (!userData) {
      return throwError(() => new Error('User data not found in storage'));
    }

    // Active account, not accounts[0]: pinning to the first row ignores which merchant
    // the user actually selected. See AccountStore.resolveAccountId.
    const accountId = this.accountStore.resolveAccountId();

    const payload = {
      merchant_id: userData.merchantId,
      account_id: accountId,
      merchant_user_id: userData.merchantUserId,
      user_type: 'merchant',
      device_type: 'web',
      token: userData.token,
    };

    return this.http
      .post<FilterResponse>(`${environment.apiUrl}/api/gozem_money/payments/get_filters`, payload)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          // Handle 429 Too Many Requests error
          if (error.status === 429) {
            this.notification.error('Too Many Requests', 'Error');
            this.logger.error('Rate limit exceeded for filter options API', error);
          } else {
            this.logger.error('Filter options API error', error);
          }
          return throwError(() => error);
        }),
      );
  }
}
